import React, { useState } from 'react'
import { Settings as SettingsIcon, Volume2, Bell, Palette, Trash2, Download, Upload, HardDrive } from 'lucide-react'
import { useApp } from '../context/AppContext.jsx'
import toast from 'react-hot-toast'

function Settings() {
  const { 
    appSettings, 
    updateAppSettings, 
    audioFiles, 
    bookmarks,
    clearAllData
  } = useApp()
  
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  
  const handleSettingChange = (setting, value) => {
    updateAppSettings({ [setting]: value })
  }
  
  const handleExportData = () => {
    try {
      const exportData = {
        audioFiles: audioFiles.map(audio => ({
          ...audio,
          audioUrl: null // Don't export blob URLs
        })),
        bookmarks,
        appSettings,
        exportedAt: new Date().toISOString(),
        version: '1.0.0'
      }
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      })
      
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `voice-converter-backup-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      toast.success('Data exported successfully!')
    } catch (error) {
      toast.error('Failed to export data')
      console.error(error)
    }
  }
  
  const handleImportData = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target?.result)
        
        if (importData.bookmarks) {
          localStorage.setItem('voiceConverter_bookmarks', JSON.stringify(importData.bookmarks))
        }
        
        if (importData.appSettings) {
          updateAppSettings(importData.appSettings)
        }
        
        toast.success('Data imported successfully! Please refresh the page.')
      } catch (error) {
        toast.error('Failed to import data. Please check the file format.')
        console.error(error)
      }
    }
    
    reader.readAsText(file)
    event.target.value = '' // Reset input
  }
  
  const handleClearAllData = () => {
    if (showClearConfirm) {
      clearAllData()
      setShowClearConfirm(false)
    } else {
      setShowClearConfirm(true)
      setTimeout(() => setShowClearConfirm(false), 5000) // Auto-cancel after 5 seconds
    }
  }
  
  const calculateStorageUsage = () => {
    try {
      const audioFilesSize = JSON.stringify(audioFiles).length
      const bookmarksSize = JSON.stringify(bookmarks).length
      const settingsSize = JSON.stringify(appSettings).length
      const totalSize = audioFilesSize + bookmarksSize + settingsSize
      
      return {
        audioFiles: (audioFilesSize / 1024).toFixed(1),
        bookmarks: (bookmarksSize / 1024).toFixed(1),
        settings: (settingsSize / 1024).toFixed(1),
        total: (totalSize / 1024).toFixed(1)
      }
    } catch (error) {
      return {
        audioFiles: '0',
        bookmarks: '0',
        settings: '0',
        total: '0'
      }
    }
  }
  
  const storageUsage = calculateStorageUsage()
  
  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Settings
        </h1>
        <p className="text-gray-600">
          Customize your app preferences and manage your data.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Audio Quality */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Audio Quality
                </h2>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Audio Quality
                </label>
                <select
                  value={appSettings.audioQuality}
                  onChange={(e) => handleSettingChange('audioQuality', e.target.value)}
                  className="input-field"
                >
                  <option value="low">Low Quality (Faster)</option>
                  <option value="medium">Medium Quality</option>
                  <option value="high">High Quality (Recommended)</option>
                  <option value="ultra">Ultra Quality (Slower)</option>
                </select>
                <p className="text-sm text-gray-600 mt-1">
                  Higher quality produces better sound but takes more processing time.
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="autoSave"
                  checked={appSettings.autoSave}
                  onChange={(e) => handleSettingChange('autoSave', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="autoSave" className="text-sm font-medium text-gray-700">
                  Auto-save generated audio files
                </label>
              </div>
            </div>
          </div>
          
          {/* Notifications */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Notifications
                </h2>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="notifications"
                  checked={appSettings.notifications}
                  onChange={(e) => handleSettingChange('notifications', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="notifications" className="text-sm font-medium text-gray-700">
                  Show success and error notifications
                </label>
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="soundNotifications"
                  checked={appSettings.soundNotifications || false}
                  onChange={(e) => handleSettingChange('soundNotifications', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="soundNotifications" className="text-sm font-medium text-gray-700">
                  Play sound for notifications
                </label>
              </div>
            </div>
          </div>
          
          {/* Appearance */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Appearance
                </h2>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Theme
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {['light', 'dark', 'auto'].map((theme) => (
                    <button
                      key={theme}
                      onClick={() => handleSettingChange('theme', theme)}
                      className={`p-3 rounded-lg border-2 transition-all duration-200 capitalize ${
                        appSettings.theme === theme
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {theme}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Auto theme follows your system preference.
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="compactMode"
                  checked={appSettings.compactMode || false}
                  onChange={(e) => handleSettingChange('compactMode', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="compactMode" className="text-sm font-medium text-gray-700">
                  Compact mode (smaller UI elements)
                </label>
              </div>
            </div>
          </div>
          
          {/* Data Management */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <HardDrive className="w-5 h-5 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Data Management
                </h2>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Export Data</h3>
                  <p className="text-sm text-gray-600">Download your bookmarks and settings</p>
                </div>
                <button
                  onClick={handleExportData}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Import Data</h3>
                  <p className="text-sm text-gray-600">Restore from a backup file</p>
                </div>
                <label className="btn-secondary flex items-center gap-2 cursor-pointer">
                  <Upload className="w-4 h-4" />
                  Import
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImportData}
                    className="hidden"
                  />
                </label>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div>
                  <h3 className="font-medium text-gray-900 text-red-700">Clear All Data</h3>
                  <p className="text-sm text-gray-600">Permanently delete all audio files and bookmarks</p>
                </div>
                <button
                  onClick={handleClearAllData}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    showClearConfirm
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-red-50 text-red-700 hover:bg-red-100'
                  }`}
                >
                  <Trash2 className="w-4 h-4" />
                  {showClearConfirm ? 'Confirm Delete' : 'Clear All'}
                </button>
              </div>
              
              {showClearConfirm && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    ⚠️ This action cannot be undone. Click "Confirm Delete" again to proceed.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="space-y-6">
          {/* Storage Usage */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">
                Storage Usage
              </h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Audio Files</span>
                <span className="font-medium">{storageUsage.audioFiles} KB</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Bookmarks</span>
                <span className="font-medium">{storageUsage.bookmarks} KB</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Settings</span>
                <span className="font-medium">{storageUsage.settings} KB</span>
              </div>
              
              <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200">
                <span className="text-gray-900 font-medium">Total</span>
                <span className="font-bold">{storageUsage.total} KB</span>
              </div>
            </div>
          </div>
          
          {/* App Info */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">
                App Information
              </h3>
            </div>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Version</span>
                <span className="font-medium">1.0.0</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Audio Files</span>
                <span className="font-medium">{audioFiles.length}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Bookmarks</span>
                <span className="font-medium">{bookmarks.length}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Browser</span>
                <span className="font-medium">{navigator.userAgent.includes('Chrome') ? 'Chrome' : 'Other'}</span>
              </div>
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">
                Quick Actions
              </h3>
            </div>
            
            <div className="space-y-2">
              <a href="/text-input" className="btn-primary w-full text-center">
                Create Audio
              </a>
              <a href="/voice-settings" className="btn-secondary w-full text-center">
                Voice Settings
              </a>
              <a href="/my-audio-files" className="btn-secondary w-full text-center">
                My Files
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Settings
